import { httpPut } from "../../fetch/xhr.js";
import { UploadFile } from "../base-file.js";
import { blobToMd5 } from "../md5.js";

//#region ../base/upload/methods/multipart.ts
const MAX_PARTS = 1e3;
const MAX_PARTS_PAGE_SIZE = 50;
/**
* Calculate the size of the individual parts based on the file size and config options
*/
function calculateParts(file, desiredPartSize) {
	const partCount = Math.min(Math.ceil(file.size / desiredPartSize), MAX_PARTS);
	const partSize = Math.ceil(file.size / partCount);
	return [partSize, partCount];
}
var UploadMultiPart = class extends UploadFile {
	canPause = true;
	multiPartOptions;
	partSize = -1;
	partCount = -1;
	parts = [];
	partsProgress = {};
	partsPromise;
	page = 1;
	pageLength;
	pageCount = 1;
	getParts;
	completeUpload;
	constructor(params, opts) {
		super(params);
		this.getParts = params.getParts;
		this.completeUpload = params.completeUpload;
		this.multiPartOptions = opts.multiPartOptions;
		const [partSize, partCount] = calculateParts(this.file, this.multiPartOptions.partSize);
		this.partSize = partSize;
		this.partCount = partCount;
		this.page = 1;
		this.pageLength = Math.min(this.partCount, MAX_PARTS_PAGE_SIZE);
		this.pageCount = Math.ceil(this.partCount / this.pageLength);
	}
	_fetchParts() {
		if (this.partsPromise) {
			this._log("Already fetching parts");
			return this.partsPromise;
		}
		if (this.parts.length > this.pageLength) {
			this._log(`Parts length (${this.parts.length}) exceeds page length (${this.pageLength})`);
			return;
		}
		if (this.page > this.pageCount) {
			this._log(`No more parts to fetch, page (${this.page}) exceeds page count (${this.pageCount})`);
			return;
		}
		this._log("Fetching more parts", {
			page: this.page,
			pageCount: this.pageCount,
			pageLength: this.pageLength,
			partsLength: this.parts.length
		});
		const asset = this.getState().asset;
		if (!asset) throw new Error("Missing upload asset!");
		this.partsPromise = this.getParts({
			page: this.page,
			pageLength: this.pageLength,
			asset
		}, {
			signal: this.abortController.signal,
			debug: this.debug
		}).then((newParts) => {
			let _parts = newParts;
			const remainder = this.page * this.pageLength - this.partCount;
			if (remainder > 0) {
				this._log(`Removing extraneous parts (${remainder}) from array`);
				_parts = _parts.slice(0, this.pageLength - remainder);
			}
			this._log(`Pushing more parts (${_parts.length}) onto parts array (${this.parts.length})`);
			this.parts.push(..._parts);
			this.page++;
			this._log("Clearing parts promise");
			this.partsPromise = void 0;
		});
		return this.partsPromise;
	}
	async _getNextPart() {
		this._fetchParts();
		if (this.parts.length) {
			this._log(`Returning part (length: ${this.parts.length})`);
			return this.parts.shift();
		}
		if (this.partsPromise) {
			this._log("Waiting for more parts");
			await this.partsPromise;
			return this.parts.shift();
		}
	}
	_updateProgress(partId, partProgress) {
		this.partsProgress[partId] = partProgress;
		const allPartsProgress = Object.values(this.partsProgress).reduce((acc, progress) => {
			return acc + progress;
		}, 0);
		this.store.setState((s) => {
			s.progress = allPartsProgress / (this.partCount + 1);
		});
	}
	_startNextPart() {
		const state = this.getState();
		if (state.state === "paused") return;
		if (state.state === "finished" || state.state === "aborted" || state.state === "failed") return;
		if (state.progress >= 1) return;
		return this._getNextPart().then((nextPart) => {
			if (!nextPart) {
				this._log("No next part returned");
				return;
			}
			if ("etag" in nextPart && nextPart.etag) {
				this._log("Part is already finished", nextPart);
				this._updateProgress(nextPart.id, 1);
				return this._startNextPart();
			}
			const startBytes = (nextPart.id - 1) * this.partSize;
			const endBytes = startBytes + this.partSize;
			const data = this.file.slice(startBytes, endBytes);
			return this._putPart(nextPart, data, 0).then(() => {
				this._log("Part finished, next!");
				return this._startNextPart();
			});
		});
	}
	async _putPart(part, data, attempt) {
		try {
			const [res, md5] = await Promise.all([httpPut(part.upload_link, data, {
				signal: this.abortController.signal,
				onProgress: (progress) => {
					this._updateProgress(part.id, progress * .9);
				}
			}).then((res$1) => {
				this._updateProgress(part.id, 1);
				return res$1;
			}), blobToMd5(data).then((res$1) => {
				return res$1;
			})]);
			const etag = (res.headers.etag ?? "").replaceAll("\"", "");
			if (md5 !== etag) throw new Error("Chunk hashes do not match");
		} catch (err) {
			this._log(err);
			const state = this.getState();
			if ((state.state === "aborted" || this.abortController.signal.aborted) && !["failed", "finished"].includes(state.state)) {
				this.store.setState((s) => {
					s.state = "aborted";
				});
				return;
			}
			this.store.setState((s) => {
				s.errors = [...s.errors, err];
			});
			if (attempt >= this.multiPartOptions.maxRetries) {
				this._log("Maximum part retry attempts achieved, failing upload...");
				this.abortController.abort();
				this.store.setState((s) => {
					s.errors = [...s.errors, err];
					s.state = "failed";
				});
				super.handleUploadFailed();
				throw err;
			}
			this._log(`Retrying part after time-out (${this.multiPartOptions.timeBetweenRetries})`, part);
			await new Promise((resolve) => {
				setTimeout(resolve, this.multiPartOptions.timeBetweenRetries);
			});
			return this._putPart(part, data, attempt + 1);
		}
	}
	async _upload() {
		let currentState = this.getState((s) => s.state);
		if (currentState === "finished" || currentState === "failed" || currentState === "aborted" || currentState === "paused") return;
		const promises = [];
		const partsInProgress = Object.values(this.partsProgress).filter((p) => p < 1).length;
		const partsToStart = this.multiPartOptions.maxConcurrentParts - partsInProgress;
		this._log("Starting uploading", partsToStart, "parts");
		for (let i = 0; i < partsToStart; i++) {
			promises.push(this._startNextPart());
			await new Promise((r) => setTimeout(r, 50));
		}
		await Promise.all(promises);
		currentState = this.getState((s) => s.state);
		if (currentState === "aborted" || currentState === "failed") return;
		const allProgress = Object.values(this.partsProgress);
		if (allProgress.length === this.partCount && allProgress.every((p) => p === 1)) await this.handleUploadFinished();
	}
	async handleUploadFinished() {
		const asset = this.getState().asset;
		if (!asset) throw new Error("Missing upload asset!");
		await this.completeUpload({ asset }, {
			signal: this.abortController.signal,
			debug: this.debug
		});
		await super.handleUploadFinished();
	}
};

//#endregion
export { UploadMultiPart };